import React from 'react';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  Select,
  Box,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { useFormContext } from 'react-hook-form';

import { useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';

import type { SelectControlProps } from './types';

export const SelectControl = React.forwardRef<HTMLInputElement, any>(
  (
    {
      name: fieldName,
      label,
      helper,
      error,
      isDisabled,
      isRequired,
      rules,
      list,
      ...props
    }: SelectControlProps,
    forwardRef,
  ) => {
    const { register } = useFormContext();

    const lang = useCommonOptionsStore(state => state.lang);
    const [t] = useTranslations(lang);

    const { ref: inputRef, ...rest } = register(fieldName, {
      ...(isRequired && { required: t('requiredField') }),
      ...rules,
      onBlur: () => {
        if (props.onBlur) props.onBlur(fieldName as unknown as any);
      },
      onChange: () => {
        if (props.onChange) props.onChange(fieldName as unknown as any);
      },
    });

    const mergedRef = (e: any) => {
      inputRef(e);
      if (forwardRef) (forwardRef as any).current = e;
    };

    return (
      <Box>
        <FormControl
          isInvalid={!!error}
          isDisabled={isDisabled}
          isRequired={isRequired}
          className={cx('InputContainer')}
        >
          <FormLabel className={cx('InputLabel')}>{label}</FormLabel>
          <InputGroup>
            <Select
              className={cx('Select')}
              {...props}
              {...(rest as any)}
              ref={mergedRef}
            >
              <option value=""> </option>
              {list?.length &&
                list.map(({ value, name }: any, idx: number) => (
                  <option key={idx} value={value}>
                    {name}
                  </option>
                ))}
            </Select>
          </InputGroup>

          {helper && !error && (
            <FormHelperText className={cx('InputHelper')}>
              {helper}
            </FormHelperText>
          )}
          {error && (
            <FormErrorMessage className={cx('InputError')}>
              {error as any}
            </FormErrorMessage>
          )}
        </FormControl>
      </Box>
    );
  },
);
